/* eslint-disable jsx-a11y/control-has-associated-label */
import React, { useState } from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { withAppContext, getToken } from '@ctsu/loader';
import bindLogger from 'helpers/BindLogger';
import _ from 'lodash';
import { GridTable } from '@ctsu/ctsu-mui-grid';
import { FasTimes } from 'assets/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faPaperclip } from '@fortawesome/pro-solid-svg-icons';
import { getTrackingCodeStatus } from 'actions/packet';
import { ATCONSTANTS as ATC } from 'appconstants';
import { CanUserUploadSupportingDocs } from 'appconstants/Permission';
import FileUploader from 'components/CustomComponents/FileUploader';
import { getErrorMessage } from 'helpers/APIHelper';
import { showErrorNotification, showSuccessNotification, showWarningNotification } from 'helpers';
import PacketIButton from 'components/iButton/PacketIButton';
import SupportingDocDownload from '../../Components/SupportingDocDownload';

const COLUMNS = [
  {
    name: 'packetId',
    label: 'Packet ID',
    renderCell: ({ id, packetId }) => (
      <>
        <PacketIButton packetDbId={id} packetId={packetId} />
        <span>{packetId}</span>
      </>
    )
  },
  { name: 'receivedDate', label: 'Received Date' },
  { name: 'packetType', label: 'Type' },
  { name: 'content', label: 'Content' },
  {
    name: '',
    label: 'Download',
    align: 'center',
    renderCell: data => <SupportingDocDownload allowMultiDoc={false} packet={data} />
  }
];

function SupportingDocs({
  context, packet, onClose, trackingCodeStatus, ...props
}) {
  const { packetId, trackingBarCode } = packet;
  const [isUploading, setIsUploading] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState();
  const [content, setContent] = useState('');

  const fetchRows = () => {
    bindLogger(props.getTrackingCodeStatus, context)(trackingBarCode);
  };

  const buildNotification = (successFileList, errorFileList) => (
    <ul style={{ listStyleType: 'none' }}>
      {successFileList != null && successFileList.length > 0 && successFileList.map((item) => (
        <li key={item.split(' ').join('_')} style={{ color: '#21C15A', width: '45em' }}>
          <strong>{item}</strong>
          {' '}
          is successfully uploaded as Supporting Document. Packet ID:
          {' '}
          <strong>{packetId}</strong>
        </li>
      ))}
      {errorFileList != null && errorFileList.length > 0 && errorFileList.map((item) => (
        <li
          style={{
            color: '#AB0731',
            width: '45em'
          }}
          key={item.split(' ').join('_')}
        >
          <strong>{item}</strong>
          {' '}
          is not uploaded successfully.
        </li>
      ))}
    </ul>
  );

  const uploadEachFile = file => {
    const formData = new FormData();
    formData.append('file', file);
    const headers = {
      Authorization: `Bearer ${getToken()}`,
      filename: file.name,
      content
    };
    const url = `${process.env.REACT_APP_ORIGIN}regulatory-data-service/v1/packets/id/${packetId}/supporting/docs`;
    return fetch(url, {
      method: 'POST',
      body: formData,
      headers
    });
  };

  const onClickUpload = () => {
    setIsUploading(true);
    const allPromises = uploadedFiles.map((eachFile) => uploadEachFile(eachFile));
    Promise.allSettled(allPromises).then((results) => {
      const successFileList = [];
      const errorFileList = [];
      results.forEach((eachPromise, i) => {
        if (eachPromise.status === 'fulfilled' && eachPromise?.value?.status === 200) {
          successFileList.push(uploadedFiles[i].name);
        } else {
          errorFileList.push(uploadedFiles[i].name);
        }
      });
      if (errorFileList.length === 0) {
        showSuccessNotification(buildNotification(successFileList));
      } else if (errorFileList.length > 0 && successFileList.length === 0) {
        showErrorNotification(buildNotification(null, errorFileList));
      } else {
        showWarningNotification(buildNotification(successFileList, errorFileList));
      }
    })
      .catch((e) => {
        showErrorNotification('Documents upload was not successful');
        throw new Error(getErrorMessage(e, 'message'));
      }).finally(() => {
        fetchRows();
        setUploadedFiles(null);
        setContent('');
        setIsUploading(false);
      });
  };

  const onChangeContent = (e) => {
    setContent(e.target.value);
  };

  const buildLimitNotification = (errorFileList) => (
    <ul style={{ listStyleType: 'none' }}>
      {errorFileList != null && errorFileList.length > 0 && errorFileList.map((item) => (
        <li
          style={{
            color: '#AB0731',
            width: '45em'
          }}
          key={item.name.split(' ').join('_')}
        >
          <strong>{item.name}</strong>
          {' '}
          size is greater than 25MB.
        </li>
      ))}
    </ul>
  );
  const maxSize = 26214400;

  const checkFiles = (allFilesList, files, upFileNames) => {
    const { sizeExtendedFiles, results } = allFilesList;
    files.forEach((file) => {
      if (upFileNames.indexOf(file.name) === -1) {
        if (file.size > maxSize) {
          sizeExtendedFiles.push(file);
        } else {
          results.push(file);
        }
      }
    });
    return { sizeExtendedFiles, results };
  };

  const updateUploadFiles = (files) => {
    const allFilesList = {
      sizeExtendedFiles: [],
      results: []
    };
    let { sizeExtendedFiles, results } = allFilesList;
    setUploadedFiles(oldFiles => {
      if (!_.isEmpty(oldFiles)) {
        const upFileNames = oldFiles.map(({ name }) => name);
        const allList = checkFiles(allFilesList, files, upFileNames);
        sizeExtendedFiles = allList.sizeExtendedFiles;
        results = allList.results;
        const filesList = [...oldFiles, ...results];
        if (filesList.length > 5) {
          showErrorNotification('Maximum 5 files can be uploaded at a time.');
          return [...oldFiles];
        }
        if (sizeExtendedFiles.length > 0) {
          showErrorNotification(buildLimitNotification(sizeExtendedFiles));
        }
        return [...filesList];
      }
      files.forEach((file) => {
        if (file.size > maxSize) {
          sizeExtendedFiles.push(file);
        } else {
          results.push(file);
        }
      });
      if (sizeExtendedFiles.length > 0) {
        showErrorNotification(buildLimitNotification(sizeExtendedFiles));
      }
      return [...results];
    });
  };

  const onChangeFile = (files) => {
    if (files.length <= 5) {
      updateUploadFiles(files);
    } else {
      showErrorNotification('Maximum 5 files can be uploaded at a time.');
    }
  };

  const onClickCancel = () => {
    onClose();
  };

  const onDelete = (e) => {
    const { index } = e.currentTarget.dataset;
    const fileArr = [...uploadedFiles];
    fileArr.splice(index, 1);
    setUploadedFiles([...fileArr]);
  };

  const loadFiles = (files) => files.map((file, i) => {
    const name = file.name ? file.name : file.attachmentFileName;
    return (
      <div key={name.split(' ').join('_')} className="file-container p-1 m-1">
        {file.path && (
          <span>
            {name}
          </span>
        )}
        <button
          disabled={false}
          data-index={i}
          onClick={onDelete}
          type="button"
          className="fn-btn font-lg ml-2"
          title="Delete"
        >
          <FasTimes />
        </button>
      </div>
    );
  });

  return (
    <>
      <span className="content-group-title">
        Tracking Code:
        <span>{trackingBarCode}</span>
      </span>
      <div className="mt-3">
        <GridTable
          tag={`${ATC.PACKETID}SUPPORTING_DOCS_BROWSER${ATC.TABLE}`}
          columns={COLUMNS}
          rows={trackingCodeStatus}
          rowsPerPageOptions={[5, 10]}
          recordPerPage={5}
          getToken={getToken}
          toPdf={false}
        />
      </div>
      {CanUserUploadSupportingDocs() ? (
        <>
          <span className="content-group-title">
            <FontAwesomeIcon icon={faPaperclip} className="mr-1" />
            Add Supporting Document
          </span>
          <span> (The size of the file uploaded shall not exceed 25MB)</span>
          <FileUploader uploadedFile={uploadedFiles} onChange={onChangeFile} multi />
          {uploadedFiles && uploadedFiles.length > 0 && (
            <li style={{ display: 'flex' }}>
              {loadFiles(uploadedFiles)}
            </li>
          )}
          <div className="mt-3">
            <span className="content-group-title">Content</span>
            <div className="mt-2">
              <textarea
                data-id={`${ATC.PACKETID}SUPPORTING_DOC_CONTENT${ATC.TEXTAREA}`}
                type="text"
                name="content"
                className="form-control bg-transparent"
                value={content}
                onChange={onChangeContent}
                placeholder=""
              />
            </div>
          </div>
          <div className="mt-3 align-center">
            <button
              data-id={`${ATC.PACKETID}SUPPORTING_DOC_UPLOAD${ATC.BUTTON}`}
              type="button"
              className="btn mx-1"
              onClick={onClickUpload}
              disabled={_.isEmpty(uploadedFiles)}
            >
              {isUploading ? <FontAwesomeIcon icon={faSpinner} spin className="mr-1" /> : null}
              Upload
            </button>
            <button
              data-id={`${ATC.PACKETID}SUPPORTING_DOC_CANCEL${ATC.BUTTON}`}
              type="button"
              className="btn close mx-1"
              onClick={onClickCancel}
            >
              Cancel
            </button>
          </div>
        </>
      ) : null}
    </>
  );
}

const mapStateToProps = (state) => ({
  trackingCodeStatus: state.packet.trackingCodeStatus
});

const withConnectState = connect(mapStateToProps, (dispatch) => bindActionCreators(
  {
    getTrackingCodeStatus
  },
  dispatch
));
export default withAppContext(withConnectState(SupportingDocs));
